"use client";

import { RichTextRenderer } from "@scott/contentful/rich-text-renderer";
import type { ContentfulHeadlineBlock } from "@scott/contentful/types";
import { cva, type VariantProps } from "class-variance-authority";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TextD2C } from "../../elements/text-d2c/text";
import {
  type VliteOptions,
  VlitePlayer,
  type VlitePlayerType,
} from "../../elements/video";
import "vlitejs/vlite.css";
import "./video.css";
import {
  IconVideoCheck,
  IconVideoChevronLeft,
  IconVideoChevronRight,
  IconVideoPause,
  IconVideoPip,
  IconVideoPlay,
  IconVideoSeekBack,
  IconVideoSeekForward,
  IconVideoSpeed,
  IconVideoSubtitles,
  IconVideoVolumeHigh,
  IconVideoVolumeLow,
  IconVideoVolumeMuted,
} from "../../icons";
import { Column, Container, Row } from "../../layout/grid";
import { cn } from "../../lib/utils";
import type { VideoBlockInspectorProps } from "./video.preview";

export interface SubtitleTrack {
  src: string;
  srclang: string;
  label: string;
  default?: boolean;
}

const SEEK_SECONDS = 10;
const MIN_VISIBLE_PLAYBACK_RATIO = 0.15;
const VOLUME_LOW_THRESHOLD = 0.5;
const VOLUME_PERCENTAGE_SCALE = 100;
const COARSE_POINTER_QUERY = "(hover: none), (pointer: coarse)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

type ProgressColor = "darkteal" | "blazeorange" | "neonchartreuse";

const videoBlockVariants = cva("", {
  variants: {
    background: {
      white: "bg-white",
      whitesmoke: "bg-whitesmoke",
      black: "bg-black",
    },
    progressColor: {
      darkteal: "video-progress-darkteal",
      blazeorange: "video-progress-blazeorange",
      neonchartreuse: "video-progress-neonchartreuse",
    },
  },
  defaultVariants: {
    background: "black",
    progressColor: "blazeorange",
  },
});

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(query).matches
  );

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return;
    }

    const mediaQuery = window.matchMedia(query);
    const updateMatches = () => setMatches(mediaQuery.matches);
    updateMatches();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateMatches);
      return () => {
        mediaQuery.removeEventListener("change", updateMatches);
      };
    }

    mediaQuery.addListener(updateMatches);
    return () => {
      mediaQuery.removeListener(updateMatches);
    };
  }, [query]);

  return matches;
}

function useCoarsePointer() {
  return useMediaQuery(COARSE_POINTER_QUERY);
}

export interface VideoBlockProps
  extends VariantProps<typeof videoBlockVariants> {
  sys?: { id: string };
  background?: "black" | "white" | "whitesmoke";
  fullWidth?: boolean;
  headline?: ContentfulHeadlineBlock;
  /** CSS color (e.g. a hex like `#FF6600`) for the headline. Defaults to white. */
  headlineColor?: string;
  src: string;
  poster?: string;
  /** Accent color for the seek-progress bar and scrubber thumb. */
  progressColor?: ProgressColor;
  /** Enables background-video mode; autoplay and looping follow reduced-motion preferences. */
  isBackgroundVideo?: boolean;
  /** Adds a play/pause button when background-video mode is active. */
  showBackgroundPlaybackControl?: boolean;
  /** Accessible label for the background-video play control. */
  playBackgroundVideoText?: string;
  /** Accessible label for the background-video pause control. */
  pauseBackgroundVideoText?: string;
  options?: VliteOptions;
  paddingTop?: "none" | "small" | "medium" | "large";
  paddingBottom?: "none" | "small" | "medium" | "large";
  subtitles?: SubtitleTrack[];
  onPlay?: (event: Event) => void;
  onPause?: (event: Event) => void;
  onEnded?: (event: Event) => void;
  onReady?: (player: VlitePlayerType) => void;
  className?: string;
  inspectorProps?: VideoBlockInspectorProps;
}

/* ─── Custom controls rendered via React portal into the vlite control bar ── */

interface VolumeControlProps {
  isMuted: boolean;
  onVolumeInteractionEnd: () => void;
  onVolumeInteractionStart: () => void;
  onToggleMute: () => void;
  onVolumeChange: (v: number) => void;
  volume: number;
}

const VOLUME_RANGE_KEYS = new Set([
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "End",
  "Home",
  "PageDown",
  "PageUp",
]);

function getVolumeIcon(isMuted: boolean, volume: number) {
  if (isMuted) {
    return <IconVideoVolumeMuted />;
  }
  if (volume <= VOLUME_LOW_THRESHOLD) {
    return <IconVideoVolumeLow />;
  }
  return <IconVideoVolumeHigh />;
}

function VolumeControl(props: Readonly<VolumeControlProps>) {
  return (
    <div className="v-volume-control">
      <button
        aria-label={props.isMuted ? "Unmute" : "Mute"}
        className="v-control-btn"
        onClick={props.onToggleMute}
        type="button"
      >
        {getVolumeIcon(props.isMuted, props.volume)}
      </button>
      <div className="v-volume-slider-popup">
        <input
          aria-label="Volume"
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={Math.round(props.volume * VOLUME_PERCENTAGE_SCALE)}
          className="v-volume-range"
          max={VOLUME_PERCENTAGE_SCALE}
          min={0}
          onBlur={props.onVolumeInteractionEnd}
          onChange={(e) =>
            props.onVolumeChange(
              Number(e.target.value) / VOLUME_PERCENTAGE_SCALE
            )
          }
          onKeyDown={(e) => {
            if (VOLUME_RANGE_KEYS.has(e.key)) {
              props.onVolumeInteractionStart();
            }
          }}
          onKeyUp={(e) => {
            if (VOLUME_RANGE_KEYS.has(e.key)) {
              props.onVolumeInteractionEnd();
            }
          }}
          onPointerCancel={props.onVolumeInteractionEnd}
          onPointerDown={props.onVolumeInteractionStart}
          onPointerUp={props.onVolumeInteractionEnd}
          step={1}
          type="range"
          value={Math.round(props.volume * VOLUME_PERCENTAGE_SCALE)}
        />
      </div>
    </div>
  );
}

/* ─── Playback speed options ────────────────────────────────────────────────── */

const PLAYBACK_SPEEDS = [
  { value: 0.5, label: "0.5×" },
  { value: 0.75, label: "0.75×" },
  { value: 1, label: "Normal" },
  { value: 1.25, label: "1.25×" },
  { value: 1.5, label: "1.5×" },
  { value: 2, label: "2×" },
] as const;
const DEFAULT_PLAYBACK_SPEED_VALUES = PLAYBACK_SPEEDS.map((s) => s.value);

type SettingsSubmenu = "subtitles" | "speed" | null;

interface VliteProviderInstance {
  setPlaybackRate?: (speed: number) => void | Promise<void>;
  getPlaybackRate?: () => number | Promise<number>;
  getAvailablePlaybackRates?: () => number[];
  requestPictureInPicture?: () => void | Promise<void>;
  exitPictureInPicture?: () => void | Promise<void>;
  getPictureInPicture?: () => boolean | Promise<boolean>;
}

interface SettingsControlProps {
  activeSubtitleTrack: number;
  availablePlaybackSpeeds: number[];
  playbackSpeed: number;
  media: HTMLVideoElement | null;
  onPlaybackSpeedChange: (speed: number) => void;
  onTogglePiP: () => void;
  onSubtitleChange: (index: number) => void;
  supportsPiP: boolean;
  supportsPlaybackSpeed: boolean;
}

function SettingsControl(props: Readonly<SettingsControlProps>) {
  const [state, setState] = useState({
    isOpen: false,
    activeSubmenu: null as SettingsSubmenu,
    tracks: [] as Array<{ index: number; label: string }>,
  });
  const menuRef = useRef<HTMLDivElement>(null);

  // Collect available subtitle / caption tracks
  useEffect(() => {
    if (!props.media) {
      return;
    }
    const media = props.media;
    const collectTracks = () => {
      const list: Array<{ index: number; label: string }> = [];
      for (let i = 0; i < media.textTracks.length; i++) {
        const t = media.textTracks[i];
        if (t.kind === "subtitles" || t.kind === "captions") {
          list.push({ index: i, label: t.label || `Track ${i + 1}` });
        }
      }
      setState((prev) => ({ ...prev, tracks: list }));
    };
    collectTracks();
    media.textTracks.addEventListener("addtrack", collectTracks);
    media.textTracks.addEventListener("removetrack", collectTracks);
    return () => {
      media.textTracks.removeEventListener("addtrack", collectTracks);
      media.textTracks.removeEventListener("removetrack", collectTracks);
    };
  }, [props.media]);

  // Close menu on click-outside or Escape
  useEffect(() => {
    if (!state.isOpen) {
      return;
    }
    const onMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setState((prev) => ({ ...prev, isOpen: false, activeSubmenu: null }));
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (state.activeSubmenu) {
          setState((prev) => ({ ...prev, activeSubmenu: null }));
        } else {
          setState((prev) => ({ ...prev, isOpen: false }));
        }
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [state.isOpen, state.activeSubmenu]);

  const handlePiP = () => {
    props.onTogglePiP();
    setState((prev) => ({ ...prev, isOpen: false, activeSubmenu: null }));
  };

  const handleSubtitleSelect = (index: number) => {
    props.onSubtitleChange(index);
    setState((prev) => ({ ...prev, activeSubmenu: null }));
  };

  const handleSubtitleOff = () => {
    props.onSubtitleChange(-1);
    setState((prev) => ({ ...prev, activeSubmenu: null }));
  };

  const handleSpeedSelect = (speed: number) => {
    props.onPlaybackSpeedChange(speed);
    setState((prev) => ({
      ...prev,
      activeSubmenu: null,
    }));
  };

  const hasSubtitles = state.tracks.length > 0;
  const speedOptions = PLAYBACK_SPEEDS.filter(
    (speed) =>
      props.availablePlaybackSpeeds.length === 0 ||
      props.availablePlaybackSpeeds.includes(speed.value)
  );
  const activeTrackLabel =
    props.activeSubtitleTrack === -1
      ? "Off"
      : (state.tracks.find((t) => t.index === props.activeSubtitleTrack)
          ?.label ?? "On");
  const activeSpeedLabel =
    PLAYBACK_SPEEDS.find((s) => s.value === props.playbackSpeed)?.label ??
    "Normal";

  return (
    <div className="v-settings-control" ref={menuRef}>
      <button
        aria-expanded={state.isOpen}
        aria-haspopup="menu"
        aria-label="Player settings"
        className="v-control-btn v-settings-btn"
        onClick={() =>
          setState((prev) => ({
            ...prev,
            isOpen: !prev.isOpen,
            activeSubmenu: null,
          }))
        }
        type="button"
      />
      {state.isOpen && (
        <div className="v-settings-menu" role="menu">
          {state.activeSubmenu === null && (
            <>
              {hasSubtitles && (
                <button
                  className="v-settings-menu-row"
                  onClick={() =>
                    setState((prev) => ({
                      ...prev,
                      activeSubmenu: "subtitles",
                    }))
                  }
                  role="menuitem"
                  type="button"
                >
                  <span className="v-settings-row-icon">
                    <IconVideoSubtitles />
                  </span>
                  <span className="v-settings-row-label">Subtitles</span>
                  <span className="v-settings-row-value">
                    {activeTrackLabel}
                  </span>
                  <span className="v-settings-row-chevron">
                    <IconVideoChevronRight />
                  </span>
                </button>
              )}
              {props.supportsPlaybackSpeed && (
                <button
                  className="v-settings-menu-row"
                  onClick={() =>
                    setState((prev) => ({ ...prev, activeSubmenu: "speed" }))
                  }
                  role="menuitem"
                  type="button"
                >
                  <span className="v-settings-row-icon">
                    <IconVideoSpeed />
                  </span>
                  <span className="v-settings-row-label">Playback speed</span>
                  <span className="v-settings-row-value">
                    {activeSpeedLabel}
                  </span>
                  <span className="v-settings-row-chevron">
                    <IconVideoChevronRight />
                  </span>
                </button>
              )}
              {props.supportsPiP && (
                <button
                  className="v-settings-menu-row"
                  onClick={handlePiP}
                  role="menuitem"
                  type="button"
                >
                  <span className="v-settings-row-icon">
                    <IconVideoPip />
                  </span>
                  <span className="v-settings-row-label">
                    Picture in Picture
                  </span>
                </button>
              )}
            </>
          )}
          {state.activeSubmenu === "subtitles" && (
            <>
              <button
                className="v-settings-menu-back"
                onClick={() =>
                  setState((prev) => ({ ...prev, activeSubmenu: null }))
                }
                type="button"
              >
                <IconVideoChevronLeft />
                <span>Subtitles</span>
              </button>
              <div aria-hidden="true" className="v-settings-menu-divider" />
              <button
                aria-checked={props.activeSubtitleTrack === -1}
                className="v-settings-menu-option"
                onClick={handleSubtitleOff}
                role="menuitemradio"
                type="button"
              >
                {props.activeSubtitleTrack === -1 && <IconVideoCheck />}
                <span>Off</span>
              </button>
              {state.tracks.map((track) => (
                <button
                  aria-checked={props.activeSubtitleTrack === track.index}
                  className="v-settings-menu-option"
                  key={track.index}
                  onClick={() => handleSubtitleSelect(track.index)}
                  role="menuitemradio"
                  type="button"
                >
                  {props.activeSubtitleTrack === track.index && (
                    <IconVideoCheck />
                  )}
                  <span>{track.label}</span>
                </button>
              ))}
            </>
          )}
          {state.activeSubmenu === "speed" && (
            <>
              <button
                className="v-settings-menu-back"
                onClick={() =>
                  setState((prev) => ({ ...prev, activeSubmenu: null }))
                }
                type="button"
              >
                <IconVideoChevronLeft />
                <span>Playback speed</span>
              </button>
              <div aria-hidden="true" className="v-settings-menu-divider" />
              {speedOptions.map((speed) => (
                <button
                  aria-checked={props.playbackSpeed === speed.value}
                  className="v-settings-menu-option"
                  key={speed.value}
                  onClick={() => handleSpeedSelect(speed.value)}
                  role="menuitemradio"
                  type="button"
                >
                  {props.playbackSpeed === speed.value && <IconVideoCheck />}
                  <span>{speed.label}</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface CustomControlsProps {
  activeSubtitleTrack: number;
  availablePlaybackSpeeds: number[];
  isMuted: boolean;
  media: HTMLVideoElement | null;
  onPlaybackSpeedChange: (speed: number) => void;
  onTogglePiP: () => void;
  onToggleMute: () => void;
  onSubtitleChange: (index: number) => void;
  onVolumeInteractionEnd: () => void;
  onVolumeInteractionStart: () => void;
  onVolumeChange: (v: number) => void;
  playbackSpeed: number;
  supportsPiP: boolean;
  supportsPlaybackSpeed: boolean;
  volume: number;
}

function CustomControls(props: Readonly<CustomControlsProps>) {
  return (
    <>
      <VolumeControl
        isMuted={props.isMuted}
        onToggleMute={props.onToggleMute}
        onVolumeChange={props.onVolumeChange}
        onVolumeInteractionEnd={props.onVolumeInteractionEnd}
        onVolumeInteractionStart={props.onVolumeInteractionStart}
        volume={props.volume}
      />
      <SettingsControl
        activeSubtitleTrack={props.activeSubtitleTrack}
        availablePlaybackSpeeds={props.availablePlaybackSpeeds}
        media={props.media}
        onPlaybackSpeedChange={props.onPlaybackSpeedChange}
        onSubtitleChange={props.onSubtitleChange}
        onTogglePiP={props.onTogglePiP}
        playbackSpeed={props.playbackSpeed}
        supportsPiP={props.supportsPiP}
        supportsPlaybackSpeed={props.supportsPlaybackSpeed}
      />
    </>
  );
}

/* ─── Provider detection ────────────────────────────────────────────────────── */

type VliteProviderName = "youtube" | "vimeo" | "cloudinary";

type ParsedVideoSrc =
  | { provider: VliteProviderName; videoId: string }
  | { provider: null; videoId: null };

const YOUTUBE_PROVIDER_PARAMS: Record<string, unknown> = {
  controls: 0,
  disablekb: 1,
  fs: 0,
  iv_load_policy: 3,
  modestbranding: 1,
  playsinline: 1,
  rel: 0,
  showinfo: 0,
};

const VIMEO_PROVIDER_PARAMS: Record<string, unknown> = {
  // Remove Vimeo chrome and metadata overlays.
  controls: false,
  title: false,
  byline: false,
  portrait: false,
  badge: false,
  vimeo_logo: false,
};

const CLOUDINARY_PROVIDER_PARAMS: Record<string, unknown> = {
  // Keep Cloudinary chrome hidden so VideoBlock controls remain primary.
  controls: false,
  bigPlayButton: false,
  showLogo: false,
  pictureInPictureToggle: false,
};

const PROVIDER_DEFAULTS: Record<VliteProviderName, Record<string, unknown>> = {
  youtube: YOUTUBE_PROVIDER_PARAMS,
  vimeo: VIMEO_PROVIDER_PARAMS,
  cloudinary: CLOUDINARY_PROVIDER_PARAMS,
};

function normalizeCloudinaryPublicId(publicId: string): string {
  return publicId
    .split("/")
    .filter((segment) => segment.length > 0)
    .map((segment) => encodeURIComponent(segment))
    .join("/");
}

function resolveCloudinaryVideoSrc(src: string): string | null {
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(src);
  } catch {
    return null;
  }

  const hostname = parsedUrl.hostname.toLowerCase();
  if (
    hostname === "res.cloudinary.com" &&
    parsedUrl.pathname.includes("/video/")
  ) {
    return src;
  }

  if (hostname !== "player.cloudinary.com") {
    return null;
  }

  const cloudName = parsedUrl.searchParams.get("cloud_name");
  const publicId = parsedUrl.searchParams.get("public_id");
  if (!(cloudName && publicId)) {
    return null;
  }

  const normalizedPublicId = normalizeCloudinaryPublicId(publicId);
  if (!normalizedPublicId) {
    return null;
  }

  return `https://res.cloudinary.com/${encodeURIComponent(cloudName)}/video/upload/${normalizedPublicId}`;
}

function parseVideoSrc(src: string): ParsedVideoSrc {
  const ytMatch = src.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&?/\s]+)/
  );
  if (ytMatch) {
    return { provider: "youtube", videoId: ytMatch[1] };
  }

  const vimeoMatch = src.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) {
    return { provider: "vimeo", videoId: vimeoMatch[1] };
  }

  const cloudinarySrc = resolveCloudinaryVideoSrc(src);
  if (cloudinarySrc) {
    return { provider: "cloudinary", videoId: cloudinarySrc };
  }

  return { provider: null, videoId: null };
}

// Map from provider name to its in-flight or completed load promise.
// Using a promise-per-provider ensures that when multiple VideoBlock instances
// mount simultaneously with the same provider, they all share one load operation
// instead of triggering duplicate registerProvider() calls (which would throw).
const providerLoadPromises = new Map<VliteProviderName, Promise<void>>();

function resolveProviderOptions(
  options: VliteOptions,
  provider: VliteProviderName | null
): VliteOptions {
  if (!provider) {
    return options;
  }

  const providerDefaults = PROVIDER_DEFAULTS[provider];

  return {
    ...options,
    providerParams: {
      ...providerDefaults,
      ...(options.providerParams ?? {}),
    },
  };
}

function clampVolume(volume: number): number {
  return Math.min(1, Math.max(0, volume));
}

function getVliteOptionsKey(options: VliteOptions): string {
  try {
    return JSON.stringify(options);
  } catch {
    return "";
  }
}

function getProviderInstance(
  player: VlitePlayerType | null
): VliteProviderInstance | null {
  if (!(player && typeof player.getInstance === "function")) {
    return null;
  }

  const instance = player.getInstance();
  if (!(instance && typeof instance === "object")) {
    return null;
  }

  return instance as VliteProviderInstance;
}

function getPlayerEventTarget(
  player: VlitePlayerType | null
): EventTarget | null {
  if (!player) {
    return null;
  }

  const directContainer = (player as { container?: unknown }).container;
  if (
    directContainer &&
    typeof directContainer === "object" &&
    "addEventListener" in directContainer
  ) {
    return directContainer as EventTarget;
  }

  const nestedContainer = (player as { elements?: { container?: unknown } })
    .elements?.container;
  if (
    nestedContainer &&
    typeof nestedContainer === "object" &&
    "addEventListener" in nestedContainer
  ) {
    return nestedContainer as EventTarget;
  }

  return null;
}

async function _loadAndRegisterProvider(
  provider: VliteProviderName
): Promise<void> {
  let module: { default?: unknown; [key: string]: unknown };

  switch (provider) {
    case "youtube":
      module = await import("vlitejs/providers/youtube");
      break;
    case "vimeo":
      module = await import("vlitejs/providers/vimeo");
      break;
    case "cloudinary":
      module = await import(
        "../../elements/video/providers/cloudinary-provider"
      );
      break;
    default:
      throw new Error(`Unsupported provider: ${provider}`);
  }

  const vlite = await import("vlitejs");
  let registerProvider: ((name: string, provider: unknown) => void) | undefined;
  if (typeof vlite.registerProvider === "function") {
    registerProvider = vlite.registerProvider;
  } else if (typeof vlite.default?.registerProvider === "function") {
    registerProvider = vlite.default.registerProvider;
  }

  if (!registerProvider) {
    throw new Error("vlitejs :: registerProvider is not available");
  }

  const providerEntry =
    provider === "cloudinary"
      ? module.CloudinaryProvider
      : module.default || module;
  registerProvider(provider, providerEntry);
}

/**
 * Loads and registers a vlitejs provider exactly once, regardless of how many
 * VideoBlock instances request it simultaneously.  Concurrent callers all
 * receive the same Promise so only one registerProvider() call is made.
 */
function loadVliteProvider(provider: VliteProviderName): Promise<void> {
  const existing = providerLoadPromises.get(provider);
  if (existing) {
    return existing;
  }
  const promise = _loadAndRegisterProvider(provider);
  providerLoadPromises.set(provider, promise);
  return promise;
}

/* ─── Default poster helpers ─────────────────────────────────────────────────
 *
 * Derive a sensible poster image for each provider without requiring the
 * consumer to specify one explicitly.  The user-supplied `poster` prop always
 * takes precedence.
 * ─────────────────────────────────────────────────────────────────────────── */

function getYouTubeDefaultPoster(videoId: string): string {
  return `https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/maxresdefault.jpg`;
}

/**
 * Builds a Cloudinary image URL that captures the middle frame of the video
 * using the `so_auto` (seek offset auto) transformation.
 */
function getCloudinaryDefaultPoster(videoSrc: string): string | null {
  try {
    const url = new URL(videoSrc);
    const segments = url.pathname.split("/");
    const uploadIdx = segments.indexOf("upload");
    if (uploadIdx === -1) {
      return null;
    }
    // Insert the so_auto transformation right after 'upload'
    segments.splice(uploadIdx + 1, 0, "so_auto");
    // Change (or add) the file extension to .jpg
    const last = segments.at(-1);
    segments[segments.length - 1] = last?.replace(/(\.[\w]+)?$/, ".jpg") || "";
    url.pathname = segments.join("/");
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * Fetches the thumbnail URL for a Vimeo video via the public oEmbed API.
 * Returns null on any network or parsing error.
 */
async function fetchVimeoDefaultPoster(
  videoId: string
): Promise<string | null> {
  try {
    const response = await fetch(
      `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(`https://vimeo.com/${videoId}`)}`
    );
    if (!response.ok) {
      return null;
    }
    const data = (await response.json()) as { thumbnail_url?: string };
    return data.thumbnail_url ?? null;
  } catch {
    return null;
  }
}

/* ─── External provider player ─────────────────────────────────────────────── */

interface ExternalVlitePlayerProps {
  videoId: string;
  provider: VliteProviderName;
  options?: VliteOptions;
  onReady?: (player: VlitePlayerType) => void;
  onPlay?: (event: Event) => void;
  onPause?: (event: Event) => void;
  onEnded?: (event: Event) => void;
}

/**
 * Renders the container div for an external provider (YouTube, Vimeo, Cloudinary)
 * and initialises vlitejs directly so that we can set the element `id` attribute
 * required by external providers.
 */
function ExternalVlitePlayer(props: Readonly<ExternalVlitePlayerProps>) {
  const rawId = useId();
  // useId returns ":r0:"; sanitise to a valid HTML id (no colons)
  const elementId = `vlite${rawId.replaceAll(":", "")}`;
  const hostRef = useRef<HTMLDivElement>(null);
  const callbacksRef = useRef(props);

  useEffect(() => {
    callbacksRef.current = props;
  });

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }

    const providerContainer = document.createElement("div");
    const dataAttr = `data-${props.provider}-id`;
    providerContainer.dataset.provider = props.provider;
    providerContainer.dataset.testid = "vlite-player";
    providerContainer.dataset.videoId = props.videoId;
    providerContainer.id = elementId;
    providerContainer.setAttribute(dataAttr, props.videoId);
    host.appendChild(providerContainer);

    let cancelled = false;
    let cleanup: (() => void) | null = null;

    import("vlitejs")
      .then((vliteModule) => {
        if (cancelled || !hostRef.current) {
          return;
        }

        type VliteConstructorType = new (
          container: HTMLElement,
          options: {
            options?: VliteOptions;
            provider?: string;
            onReady?: (player: VlitePlayerType) => void;
          }
        ) => { container: EventTarget; destroy: () => void };
        const Vlitejs = (vliteModule.default ??
          vliteModule) as VliteConstructorType;

        const instance = new Vlitejs(providerContainer, {
          options: callbacksRef.current.options,
          provider: callbacksRef.current.provider,
          onReady: (player: VlitePlayerType) => {
            if (!cancelled) {
              callbacksRef.current.onReady?.(player);
            }
          },
        });

        const vliteContainer: EventTarget = instance.container;
        const onPlay = (e: Event) => callbacksRef.current.onPlay?.(e);
        const onPause = (e: Event) => callbacksRef.current.onPause?.(e);
        const onEnded = (e: Event) => callbacksRef.current.onEnded?.(e);

        vliteContainer.addEventListener("play", onPlay);
        vliteContainer.addEventListener("pause", onPause);
        vliteContainer.addEventListener("ended", onEnded);

        cleanup = () => {
          vliteContainer.removeEventListener("play", onPlay);
          vliteContainer.removeEventListener("pause", onPause);
          vliteContainer.removeEventListener("ended", onEnded);
          instance.destroy();
          host.replaceChildren();
        };
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      cleanup?.();
      if (!cleanup) {
        host.replaceChildren();
      }
    };
    // provider and videoId changes warrant full re-init; other props are read via ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementId, props.provider, props.videoId]);

  return <div ref={hostRef} />;
}

/* ─── Subtitle overlay (custom VTT cue rendering) ──────────────────────────── */

interface SubtitleOverlayProps {
  activeSubtitleTrack: number;
  controlsVisible: boolean;
  media: HTMLVideoElement;
}

function SubtitleOverlay(props: Readonly<SubtitleOverlayProps>) {
  const [cueText, setCueText] = useState<string | null>(null);

  useEffect(() => {
    if (props.activeSubtitleTrack === -1) {
      setCueText(null);
      return;
    }
    const track = props.media.textTracks[props.activeSubtitleTrack];
    if (!track) {
      setCueText(null);
      return;
    }
    const onCueChange = () => {
      const active = track.activeCues;
      if (active && active.length > 0) {
        const cue = active[0] as VTTCue;
        setCueText(cue.text.replaceAll(/<[^<>]*>/g, ""));
      } else {
        setCueText(null);
      }
    };
    track.addEventListener("cuechange", onCueChange);
    onCueChange();
    return () => track.removeEventListener("cuechange", onCueChange);
  }, [props.media, props.activeSubtitleTrack]);

  if (!cueText) {
    return null;
  }

  return (
    <div
      aria-atomic="true"
      aria-live="polite"
      className={cn(
        "v-subtitle-overlay",
        props.controlsVisible && "v-subtitle-overlay--controls-visible"
      )}
    >
      <div className="v-subtitle-cue">{cueText}</div>
    </div>
  );
}

/* ─── Component ────────────────────────────────────────────────────────────── */

export function VideoBlock(props: Readonly<VideoBlockProps>) {
  const vlitePlayerRef = useRef<VlitePlayerType | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastAudibleVolumeRef = useRef(1);
  const volumeInteractionLatestValueRef = useRef(1);
  const volumeInteractionStartVolumeRef = useRef<number | null>(null);
  const viewportPausedRef = useRef(false);
  const isCoarsePointer = useCoarsePointer();
  const prefersReducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);

  const parsedSrc = parseVideoSrc(props.src);

  const [state, setState] = useState({
    playerReady: false,
    isPlaying: false,
    hasStarted: false,
    isMuted: false,
    volume: 1,
    playbackSpeed: 1,
    availablePlaybackSpeeds: DEFAULT_PLAYBACK_SPEED_VALUES,
    supportsPlaybackSpeed: true,
    supportsPiP: false,
    customControlsEl: null as Element | null,
    mediaEl: null as HTMLVideoElement | null,
    providerLoaded: parsedSrc.provider === null,
    activeSubtitleTrack: -1,
    controlsVisible: false,
    /** Poster URL resolved asynchronously (currently Vimeo only). */
    defaultPoster: null as string | null,
  });

  const variantClasses = videoBlockVariants({
    background: props.background,
    progressColor: props.progressColor,
  });

  // Derive a default poster from the provider when none is supplied by the user.
  // YouTube and Cloudinary are computed synchronously; Vimeo is fetched async
  // (see the useEffect below) and stored in state.defaultPoster.
  function getSyncDefaultPoster(): string | undefined {
    if (parsedSrc.provider === "youtube") {
      return getYouTubeDefaultPoster(parsedSrc.videoId);
    }
    if (parsedSrc.provider === "cloudinary") {
      return getCloudinaryDefaultPoster(parsedSrc.videoId) ?? undefined;
    }
    return;
  }
  const syncDefaultPoster = getSyncDefaultPoster();

  const effectivePoster =
    props.poster ?? syncDefaultPoster ?? state.defaultPoster ?? undefined;

  const mergedOptions: VliteOptions = {
    autoHide: true,
    controls: true,
    playPause: true,
    progressBar: true,
    volume: true,
    fullscreen: true,
    bigPlay: false,
    playsinline: true,
    muted: false,
    loop: false,
    ...props.options,
    ...(effectivePoster ? { poster: effectivePoster } : {}),
  };

  const autoplaySafeOptions: VliteOptions = mergedOptions.autoplay
    ? {
        ...mergedOptions,
        muted: true,
      }
    : mergedOptions;

  const resolvedOptions: VliteOptions = props.isBackgroundVideo
    ? {
        ...autoplaySafeOptions,
        autoplay: !prefersReducedMotion,
        muted: true,
        loop: !prefersReducedMotion,
        controls: false,
        playPause: false,
        progressBar: false,
        volume: false,
        fullscreen: false,
        bigPlay: false,
      }
    : autoplaySafeOptions;

  // YouTube renders its own centre play button on desktop, but vlite disables
  // iframe pointer events and does not toggle playback from its touch overlay.
  // Keep a React-owned play button on coarse pointers so iOS can start playback.
  const suppressOwnPlayButton =
    parsedSrc.provider === "youtube" && !isCoarsePointer;
  const shouldShowPausedCenterControls = !(
    state.isPlaying || suppressOwnPlayButton
  );
  const centerControlsActive =
    shouldShowPausedCenterControls || state.hasStarted;

  const providerOptions = resolveProviderOptions(
    resolvedOptions,
    parsedSrc.provider
  );
  const playerOptionsKey = getVliteOptionsKey(
    parsedSrc.provider ? providerOptions : resolvedOptions
  );
  const playerKey = `${props.src}:${playerOptionsKey}`;

  const setVolumeState = useCallback(
    (
      volume: number,
      isMuted: boolean,
      options: { rememberAudible?: boolean } = {}
    ) => {
      const clampedVolume = clampVolume(volume);
      if (clampedVolume > 0 && options.rememberAudible !== false) {
        lastAudibleVolumeRef.current = clampedVolume;
      }

      setState((prev) =>
        prev.volume === clampedVolume && prev.isMuted === isMuted
          ? prev
          : { ...prev, isMuted, volume: clampedVolume }
      );
    },
    []
  );

  const syncVolumeFromPlayer = useCallback(
    (player?: VlitePlayerType | null) => {
      const instance = player ?? vlitePlayerRef.current;
      if (!instance) {
        return;
      }

      const media = instance.media;
      // vlite tracks the muted state independently of the volume level, so a
      // video initialised with `muted` keeps its volume at 1 while producing no
      // sound. Keep both pieces of state so the button can show "muted" without
      // losing the real volume level. For html5 we read the media element's
      // `muted` attribute; external providers fall back to vlite's `isMuted`.
      const isMuted =
        (media instanceof HTMLMediaElement && media.muted) ||
        instance.isMuted === true;
      const rememberAudible = volumeInteractionStartVolumeRef.current === null;

      if (typeof instance.getVolume === "function") {
        void instance
          .getVolume()
          .then((volume) => {
            setVolumeState(volume, isMuted, { rememberAudible });
          })
          .catch(() => {
            if (media instanceof HTMLMediaElement) {
              setVolumeState(media.volume, media.muted || isMuted, {
                rememberAudible,
              });
              return;
            }
            setState((prev) =>
              prev.isMuted === isMuted ? prev : { ...prev, isMuted }
            );
          });
        return;
      }

      if (media instanceof HTMLMediaElement) {
        setVolumeState(media.volume, media.muted || isMuted, {
          rememberAudible,
        });
        return;
      }

      setState((prev) =>
        prev.isMuted === isMuted ? prev : { ...prev, isMuted }
      );
    },
    [setVolumeState]
  );

  const syncPlaybackCapabilities = useCallback(
    (player: VlitePlayerType, media: HTMLVideoElement | null) => {
      const providerInstance = getProviderInstance(player);

      let availablePlaybackSpeeds = DEFAULT_PLAYBACK_SPEED_VALUES;
      if (
        !media &&
        typeof providerInstance?.getAvailablePlaybackRates === "function"
      ) {
        const providerSpeeds = providerInstance.getAvailablePlaybackRates();
        if (Array.isArray(providerSpeeds) && providerSpeeds.length > 0) {
          const normalizedProviderSpeeds = providerSpeeds.filter(
            (speed): speed is number =>
              typeof speed === "number" && Number.isFinite(speed)
          );
          const filteredSpeeds = DEFAULT_PLAYBACK_SPEED_VALUES.filter((speed) =>
            normalizedProviderSpeeds.includes(speed)
          );
          availablePlaybackSpeeds =
            filteredSpeeds.length > 0 ? filteredSpeeds : [1];
        }
      }

      const supportsProviderPlaybackSpeed =
        typeof providerInstance?.setPlaybackRate === "function";
      const supportsPlaybackSpeed =
        !!media ||
        (supportsProviderPlaybackSpeed && availablePlaybackSpeeds.length > 1);
      const supportsHtml5PiP =
        !!media &&
        typeof document !== "undefined" &&
        !!document.pictureInPictureEnabled &&
        !media.disablePictureInPicture;
      const supportsProviderPiP =
        typeof providerInstance?.requestPictureInPicture === "function";

      setState((prev) => ({
        ...prev,
        supportsPlaybackSpeed,
        supportsPiP: supportsHtml5PiP || supportsProviderPiP,
        availablePlaybackSpeeds,
      }));

      if (media) {
        setState((prev) => ({
          ...prev,
          playbackSpeed: Number.isFinite(media.playbackRate)
            ? media.playbackRate
            : 1,
        }));
        return;
      }

      if (typeof providerInstance?.getPlaybackRate === "function") {
        void Promise.resolve(providerInstance.getPlaybackRate())
          .then((playbackSpeed) => {
            if (
              typeof playbackSpeed === "number" &&
              Number.isFinite(playbackSpeed)
            ) {
              setState((prev) => ({ ...prev, playbackSpeed }));
            }
          })
          .catch(() => {});
      }
    },
    []
  );

  const handleReady = useCallback(
    (player: VlitePlayerType) => {
      vlitePlayerRef.current = player;
      viewportPausedRef.current = false;
      const rawMedia = player?.media;
      const media = rawMedia instanceof HTMLVideoElement ? rawMedia : null;
      setState((prev) => ({ ...prev, playerReady: true, mediaEl: media }));
      syncVolumeFromPlayer(player);
      syncPlaybackCapabilities(player, media);
      props.onReady?.(player);
    },
    [props.onReady, syncPlaybackCapabilities, syncVolumeFromPlayer]
  );

  const handlePlay = useCallback(
    (event: Event) => {
      viewportPausedRef.current = false;
      setState((prev) => ({ ...prev, isPlaying: true, hasStarted: true }));
      props.onPlay?.(event);
    },
    [props.onPlay]
  );

  const handlePause = useCallback(
    (event: Event) => {
      setState((prev) => ({ ...prev, isPlaying: false }));
      props.onPause?.(event);
    },
    [props.onPause]
  );

  const handleTogglePlayPause = useCallback(() => {
    const player = vlitePlayerRef.current;
    if (!player) {
      return;
    }
    if (state.isPlaying) {
      player.pause();
    } else {
      player.play();
    }
  }, [state.isPlaying]);

  const handleSeekBack = useCallback(() => {
    const player = vlitePlayerRef.current;
    if (!player) {
      return;
    }
    void player
      .getCurrentTime()
      .then((current: number) => {
        player.seekTo(Math.max(0, current - SEEK_SECONDS));
      })
      .catch(() => {});
  }, []);

  const handleSeekForward = useCallback(async () => {
    const player = vlitePlayerRef.current;
    if (!player) {
      return;
    }
    try {
      const [duration, current] = await Promise.all([
        player.getDuration(),
        player.getCurrentTime(),
      ]);
      player.seekTo(Math.min(duration, current + SEEK_SECONDS));
    } catch {
      // Intentionally ignored - non-critical user action
    }
  }, []);

  const handleVolumeChange = useCallback(
    (newVolume: number) => {
      const player = vlitePlayerRef.current;
      if (!player) {
        return;
      }

      const volume = clampVolume(newVolume);
      const isMuted = volume === 0;
      const isVolumeInteractionActive =
        volumeInteractionStartVolumeRef.current !== null;
      volumeInteractionLatestValueRef.current = volume;

      if (typeof player.setVolume === "function") {
        player.setVolume(volume);
      }

      if (isMuted) {
        player.mute?.();
      } else {
        player.unMute?.();
      }

      const media = player.media;
      if (media instanceof HTMLMediaElement) {
        media.volume = volume;
        media.muted = isMuted;
      }

      setVolumeState(volume, isMuted, {
        rememberAudible: !isVolumeInteractionActive,
      });
    },
    [setVolumeState]
  );

  const handleVolumeInteractionStart = useCallback(() => {
    if (volumeInteractionStartVolumeRef.current !== null) {
      return;
    }
    const startVolume =
      state.volume > 0 ? state.volume : lastAudibleVolumeRef.current;
    volumeInteractionStartVolumeRef.current = startVolume;
    volumeInteractionLatestValueRef.current = state.volume;
  }, [state.volume]);

  const handleVolumeInteractionEnd = useCallback(() => {
    const startVolume = volumeInteractionStartVolumeRef.current;
    if (startVolume === null) {
      return;
    }

    const latestVolume = volumeInteractionLatestValueRef.current;
    lastAudibleVolumeRef.current =
      latestVolume > 0 ? latestVolume : startVolume;
    volumeInteractionStartVolumeRef.current = null;
  }, []);

  const handleToggleMute = useCallback(() => {
    const player = vlitePlayerRef.current;
    if (!player) {
      return;
    }

    const media = player.media;
    if (state.isMuted) {
      const restoredVolume =
        state.volume > 0 ? state.volume : lastAudibleVolumeRef.current;

      if (state.volume === 0 && typeof player.setVolume === "function") {
        player.setVolume(restoredVolume);
      }
      player.unMute?.();
      if (media instanceof HTMLMediaElement) {
        media.volume = restoredVolume;
        media.muted = false;
      }
      setVolumeState(restoredVolume, false);
      return;
    }

    player.mute?.();
    if (media instanceof HTMLMediaElement) {
      media.muted = true;
    }
    setVolumeState(state.volume, true);
  }, [setVolumeState, state.isMuted, state.volume]);

  const handlePlaybackSpeedChange = useCallback((speed: number) => {
    const player = vlitePlayerRef.current;
    if (!player) {
      return;
    }

    const media = player.media;
    if (media instanceof HTMLMediaElement) {
      media.playbackRate = speed;
      setState((prev) => ({ ...prev, playbackSpeed: speed }));
      return;
    }

    const providerInstance = getProviderInstance(player);
    if (
      !(
        providerInstance &&
        typeof providerInstance.setPlaybackRate === "function"
      )
    ) {
      return;
    }

    if (typeof providerInstance.getAvailablePlaybackRates === "function") {
      const availablePlaybackSpeeds =
        providerInstance.getAvailablePlaybackRates();
      if (
        Array.isArray(availablePlaybackSpeeds) &&
        availablePlaybackSpeeds.length > 0 &&
        !availablePlaybackSpeeds.includes(speed)
      ) {
        return;
      }
    }

    void Promise.resolve(providerInstance.setPlaybackRate(speed))
      .then(() => {
        setState((prev) => ({ ...prev, playbackSpeed: speed }));
      })
      .catch(() => {});
  }, []);

  const handleTogglePiP = useCallback(() => {
    const player = vlitePlayerRef.current;
    if (!player) {
      return;
    }

    const media = player.media;
    if (
      media instanceof HTMLVideoElement &&
      typeof document !== "undefined" &&
      document.pictureInPictureEnabled &&
      !media.disablePictureInPicture
    ) {
      void (async () => {
        if (document.pictureInPictureElement) {
          await document.exitPictureInPicture();
        } else {
          await media.requestPictureInPicture();
        }
      })().catch(() => {});
      return;
    }

    const providerInstance = getProviderInstance(player);
    if (
      !(
        providerInstance &&
        typeof providerInstance.requestPictureInPicture === "function"
      )
    ) {
      return;
    }
    const requestProviderPictureInPicture =
      providerInstance.requestPictureInPicture;

    void (async () => {
      let isProviderPiPEnabled = false;
      if (typeof providerInstance.getPictureInPicture === "function") {
        try {
          isProviderPiPEnabled = Boolean(
            await Promise.resolve(providerInstance.getPictureInPicture())
          );
        } catch {}
      }

      if (
        isProviderPiPEnabled &&
        typeof providerInstance.exitPictureInPicture === "function"
      ) {
        await Promise.resolve(providerInstance.exitPictureInPicture());
        return;
      }
      await Promise.resolve(requestProviderPictureInPicture());
    })().catch(() => {});
  }, []);

  const handleSubtitleChange = (index: number) => {
    setState((prev) => ({ ...prev, activeSubtitleTrack: index }));
  };

  // Load the vlite provider on demand based on the detected URL type
  useEffect(() => {
    const { provider } = parseVideoSrc(props.src);

    if (!provider) {
      setState((prev) => ({ ...prev, providerLoaded: true }));
      return;
    }

    // Fast path: provider was already requested (promise exists and may already
    // be resolved).  Attach a then-handler so providerLoaded is set correctly
    // even when this component mounts after the first load has finished.
    const existingPromise = providerLoadPromises.get(provider);
    if (existingPromise) {
      let cancelled = false;
      existingPromise
        .then(() => {
          if (!cancelled) {
            setState((prev) => ({ ...prev, providerLoaded: true }));
          }
        })
        .catch(() => {});
      return () => {
        cancelled = true;
      };
    }

    setState((prev) => ({
      ...prev,
      playerReady: false,
      customControlsEl: null,
      mediaEl: null,
      playbackSpeed: 1,
      isMuted: false,
      supportsPlaybackSpeed: false,
      supportsPiP: false,
      availablePlaybackSpeeds: DEFAULT_PLAYBACK_SPEED_VALUES,
      providerLoaded: false,
    }));

    let cancelled = false;
    loadVliteProvider(provider)
      .then(() => {
        if (!cancelled) {
          setState((prev) => ({ ...prev, providerLoaded: true }));
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [props.src]);

  // Background motion starts paused when the OS requests reduced motion. The
  // optional playback control still lets the user start it deliberately.
  useEffect(() => {
    if (
      !(props.isBackgroundVideo && prefersReducedMotion && state.playerReady)
    ) {
      return;
    }

    viewportPausedRef.current = false;
    vlitePlayerRef.current?.pause();
  }, [prefersReducedMotion, props.isBackgroundVideo, state.playerReady]);

  // Inject a portal container inside the vlite control bar once the player is ready
  useEffect(() => {
    if (!(state.playerReady && containerRef.current)) {
      return;
    }
    const controlBar = containerRef.current.querySelector(".v-controlBar");
    if (!controlBar) {
      return;
    }
    const wrapper = document.createElement("div");
    wrapper.className = "v-custom-controls";
    controlBar.appendChild(wrapper);
    setState((prev) => ({ ...prev, customControlsEl: wrapper }));
    return () => {
      wrapper.remove();
    };
  }, [state.playerReady]);

  // Inject subtitle tracks into the video element when it becomes available
  useEffect(() => {
    if (!(state.mediaEl && props.subtitles?.length)) {
      return;
    }
    const existing = state.mediaEl.querySelectorAll("track");
    for (const el of Array.from(existing)) {
      el.remove();
    }
    let defaultIndex = -1;
    for (let i = 0; i < props.subtitles.length; i++) {
      const sub = props.subtitles[i];
      const track = document.createElement("track");
      track.kind = "subtitles";
      track.src = sub.src;
      track.srclang = sub.srclang;
      track.label = sub.label;
      state.mediaEl.appendChild(track);
      if (sub.default && defaultIndex === -1) {
        defaultIndex = i;
      }
    }
    if (defaultIndex !== -1) {
      setState((prev) => ({ ...prev, activeSubtitleTrack: defaultIndex }));
    }
  }, [state.mediaEl, props.subtitles]);

  // Sync text-track modes: active track uses "hidden" so cuechange fires without
  // native browser rendering; SubtitleOverlay handles the visual display.
  useEffect(() => {
    if (!state.mediaEl) {
      return;
    }
    for (let i = 0; i < state.mediaEl.textTracks.length; i++) {
      state.mediaEl.textTracks[i].mode =
        i === state.activeSubtitleTrack ? "hidden" : "disabled";
    }
  }, [state.mediaEl, state.activeSubtitleTrack]);

  // Observe vlitejs v-hidden class on .v-controlBar to reposition subtitles relative to the control bar.
  // vlitejs adds "v-hidden" to .v-controlBar when autoHide kicks in; removing it means controls are visible.
  useEffect(() => {
    if (!(state.playerReady && containerRef.current)) {
      return;
    }
    const controlBar = containerRef.current.querySelector(".v-controlBar");
    if (!controlBar) {
      return;
    }
    const update = () => {
      setState((prev) => ({
        ...prev,
        controlsVisible: !controlBar.classList.contains("v-hidden"),
      }));
    };
    const observer = new MutationObserver(update);
    observer.observe(controlBar, {
      attributes: true,
      attributeFilter: ["class"],
    });
    update();
    return () => observer.disconnect();
  }, [state.playerReady]);

  // Fetch the default poster from Vimeo's oEmbed API when no poster is provided.
  // YouTube and Cloudinary posters are derived synchronously above, so only
  // Vimeo requires an async fetch.  Reset defaultPoster when switching away
  // from Vimeo or when the consumer provides an explicit poster.
  useEffect(() => {
    if (parsedSrc.provider !== "vimeo" || props.poster) {
      setState((prev) =>
        prev.defaultPoster !== null ? { ...prev, defaultPoster: null } : prev
      );
      return;
    }
    const videoId = parsedSrc.videoId;
    if (!videoId) {
      return;
    }
    let cancelled = false;
    fetchVimeoDefaultPoster(videoId).then((url) => {
      if (!cancelled) {
        setState((prev) => ({ ...prev, defaultPoster: url }));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [parsedSrc.provider, parsedSrc.videoId, props.poster]);

  // Keep volume state in sync when changed through vlite APIs / native media controls.
  useEffect(() => {
    if (!state.playerReady) {
      return;
    }
    const player = vlitePlayerRef.current;
    if (!player) {
      return;
    }
    const playerEventTarget = getPlayerEventTarget(player);
    if (!playerEventTarget) {
      return;
    }
    const syncVolume = () => {
      syncVolumeFromPlayer(player);
    };

    playerEventTarget.addEventListener("volumechange", syncVolume);

    const media = player.media;
    if (media instanceof HTMLMediaElement) {
      media.addEventListener("volumechange", syncVolume);
    }

    syncVolume();

    return () => {
      playerEventTarget.removeEventListener("volumechange", syncVolume);
      if (media instanceof HTMLMediaElement) {
        media.removeEventListener("volumechange", syncVolume);
      }
    };
  }, [state.playerReady, syncVolumeFromPlayer]);

  useEffect(() => {
    if (!(state.playerReady && containerRef.current)) {
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const player = vlitePlayerRef.current;
        if (!entry) {
          return;
        }
        if (!player) {
          return;
        }

        const isVisible = entry.intersectionRatio >= MIN_VISIBLE_PLAYBACK_RATIO;

        if (!isVisible && state.isPlaying) {
          if (resolvedOptions.autoplay) {
            viewportPausedRef.current = true;
          }
          player.pause();
          return;
        }

        if (
          isVisible &&
          viewportPausedRef.current &&
          resolvedOptions.autoplay &&
          !prefersReducedMotion
        ) {
          viewportPausedRef.current = false;
          void Promise.resolve(player.play()).catch(() => {
            viewportPausedRef.current = true;
          });
        }
      },
      {
        threshold: [0, MIN_VISIBLE_PLAYBACK_RATIO],
      }
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [
    prefersReducedMotion,
    resolvedOptions.autoplay,
    state.isPlaying,
    state.playerReady,
  ]);

  const videoPlayer = (
    <div className="relative" ref={containerRef}>
      {props.headline && (
        <div
          className="px:0 pointer-events-none absolute top-6 left-6 z-10 max-w-[calc(100%-3rem)] md:top-14 md:left-14 md:max-w-[calc(100%-7rem)] md:px-10"
          {...props.inspectorProps?.headline}
        >
          <TextD2C
            color="white"
            colorHex={props.headlineColor}
            dash={true}
            variant="h3"
          >
            <RichTextRenderer richText={props.headline.headline?.json} />
          </TextD2C>
        </div>
      )}
      <div
        className={cn(
          "v-player-surface px:0 md:px-10",
          props.isBackgroundVideo && "pointer-events-none"
        )}
      >
        {state.providerLoaded &&
          (parsedSrc.provider ? (
            <ExternalVlitePlayer
              key={playerKey}
              onEnded={props.onEnded}
              onPause={handlePause}
              onPlay={handlePlay}
              onReady={handleReady}
              options={providerOptions}
              provider={parsedSrc.provider}
              videoId={parsedSrc.videoId}
            />
          ) : (
            <VlitePlayer
              key={playerKey}
              onEnded={props.onEnded}
              onPause={handlePause}
              onPlay={handlePlay}
              onReady={handleReady}
              options={resolvedOptions}
              src={props.src}
            />
          ))}
      </div>

      {state.playerReady &&
        props.isBackgroundVideo &&
        props.showBackgroundPlaybackControl && (
          <button
            aria-label={
              state.isPlaying
                ? (props.pauseBackgroundVideoText ?? "Pause background video")
                : (props.playBackgroundVideoText ?? "Play background video")
            }
            className={cn(
              "absolute top-4 right-4 z-10 grid size-11 place-items-center rounded-full",
              "bg-black/40 text-white backdrop-blur-[3px]",
              "focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
            )}
            onClick={handleTogglePlayPause}
            type="button"
          >
            {state.isPlaying ? (
              <IconVideoPause className="h-5 w-4" />
            ) : (
              <IconVideoPlay className="h-5 w-4" />
            )}
          </button>
        )}

      {state.playerReady && !props.isBackgroundVideo && (
        <div
          className={cn(
            "v-center-overlay",
            centerControlsActive && "v-center-overlay--active",
            state.isPlaying && "v-center-overlay--playing",
            state.isPlaying &&
              state.controlsVisible &&
              "v-center-overlay--controls-visible"
          )}
        >
          <button
            aria-label={`Seek back ${SEEK_SECONDS} seconds`}
            className="v-seek-button"
            onClick={handleSeekBack}
            type="button"
          >
            <IconVideoSeekBack />
          </button>

          {!suppressOwnPlayButton && (
            <button
              aria-label={state.isPlaying ? "Pause" : "Play"}
              className="v-playback-button"
              onClick={handleTogglePlayPause}
              type="button"
            >
              {state.isPlaying ? <IconVideoPause /> : <IconVideoPlay />}
            </button>
          )}

          <button
            aria-label={`Seek forward ${SEEK_SECONDS} seconds`}
            className="v-seek-button"
            onClick={handleSeekForward}
            type="button"
          >
            <IconVideoSeekForward />
          </button>
        </div>
      )}
      {state.mediaEl && (
        <SubtitleOverlay
          activeSubtitleTrack={state.activeSubtitleTrack}
          controlsVisible={state.controlsVisible}
          media={state.mediaEl}
        />
      )}
      {!props.isBackgroundVideo &&
        state.playerReady &&
        effectivePoster &&
        !state.hasStarted &&
        parsedSrc.provider !== null && (
          <div aria-hidden="true" className="v-poster-overlay">
            <img alt="" src={effectivePoster} />
          </div>
        )}
      {!props.isBackgroundVideo &&
        state.customControlsEl !== null &&
        createPortal(
          <CustomControls
            activeSubtitleTrack={state.activeSubtitleTrack}
            availablePlaybackSpeeds={state.availablePlaybackSpeeds}
            isMuted={state.isMuted}
            media={state.mediaEl}
            onPlaybackSpeedChange={handlePlaybackSpeedChange}
            onSubtitleChange={handleSubtitleChange}
            onToggleMute={handleToggleMute}
            onTogglePiP={handleTogglePiP}
            onVolumeChange={handleVolumeChange}
            onVolumeInteractionEnd={handleVolumeInteractionEnd}
            onVolumeInteractionStart={handleVolumeInteractionStart}
            playbackSpeed={state.playbackSpeed}
            supportsPiP={state.supportsPiP}
            supportsPlaybackSpeed={state.supportsPlaybackSpeed}
            volume={state.isMuted ? 0 : state.volume}
          />,
          state.customControlsEl
        )}
    </div>
  );

  return (
    <Container
      className={cn(
        variantClasses,
        "component-video-block -mb-px",
        props.className
      )}
      fullWidth={true}
      outerContainerOnly={props.fullWidth}
      paddingBottom={props.paddingBottom}
      paddingTop={props.paddingTop}
      testId="video-block"
      {...props.inspectorProps?.container}
    >
      {props.fullWidth ? (
        videoPlayer
      ) : (
        <Row>
          <Column className="col-span-10 col-start-2">{videoPlayer}</Column>
        </Row>
      )}
    </Container>
  );
}
